import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { apiClient } from '../../lib/api/client'
import { isSeriesFacetRenderable, reachableSeriesCeiling } from './catalog-series-facet-filter'
import type { CatalogCategoryFacets, CatalogFacet } from './types'

/**
 * The narrow seam this hook needs — `GET /catalog/categories/{category}/facets`
 * (UID-385 / ADR-0121), mirroring `useCatalogSeries.ts`'s `CatalogSeriesService`
 * pattern: an injectable interface so tests never hit the network.
 *
 * UID-387 — `specs` threads the facet SELECTION through: absent/empty keeps the
 * precomputed, query-independent catalogue (byte-identical to before this
 * change); a non-empty selection switches upstream to the live drilldown
 * (per-value `valueCounts`, an `appliedSpecs` echo) over the narrowed candidate
 * set. See `catalog-series-facet-filter.ts`'s `isSeriesFacetRenderable` doc
 * comment for how the render gate itself branches on the same distinction.
 */
export interface CatalogFacetsService {
  listFacets(
    category: string,
    specs?: Readonly<Record<string, string>>,
  ): Promise<CatalogCategoryFacets>
}

async function bffListFacets(
  category: string,
  specs?: Readonly<Record<string, string>>,
): Promise<CatalogCategoryFacets> {
  const { data, error } = await apiClient.GET('/catalog/categories/{category}/facets', {
    params: {
      path: { category },
      query: {
        ...(specs !== undefined && Object.keys(specs).length > 0 ? { specs } : {}),
      },
    },
  })
  if (error || !data) {
    throw new Error('We couldn’t load this category’s filters.')
  }
  return data
}

export const defaultCatalogFacetsService: CatalogFacetsService = {
  listFacets: bffListFacets,
}

export type CatalogFacetsState =
  | { readonly status: 'loading'; readonly facets: readonly CatalogFacet[] }
  | { readonly status: 'ready'; readonly facets: readonly CatalogFacet[] }

/**
 * Data-layer seam for the series-page facet sidebar: fetches the facet
 * catalogue for `category` — precomputed and query-independent when
 * `selection` is empty, a LIVE drilldown scoped to `selection` otherwise
 * (UID-387) — and narrows it to the facets this page is allowed to render a
 * control for ({@link isSeriesFacetRenderable}, itself branching on whether
 * `selection` is empty).
 *
 * `status: 'loading'` fires on the INITIAL fetch AND on every re-fetch caused
 * by a `selection` change, not just on mount — the live drilldown a non-empty
 * selection triggers has been measured up to ~5.5s for a broad selection
 * (e.g. `Brand=MISUMI`), against a 20s BFF-side timeout, so the sidebar must
 * show a visible pending state for that window rather than silently keeping
 * stale checkboxes on screen with no affordance that a narrower answer is on
 * the way. The PREVIOUS `facets` are carried on the loading state (not
 * dropped to `[]`) so a consumer can choose to keep rendering the stale list
 * under a pending treatment instead of collapsing the sidebar to nothing on
 * every keystroke-adjacent selection change.
 *
 * Deliberately has NO `error` state, unlike `useCatalogSeries`/
 * `useCategoryTaxonomy`: ADR-0120's WAF Reliability note is binding here —
 * the sidebar must degrade to an unfiltered grid on a failed catalogue read,
 * never to an empty (or error-blocked) grid, and a failed facet read must
 * never read as "this category has no products." So a rejected `listFacets`
 * resolves to the same `ready`/`facets: []` state as a genuinely empty
 * catalogue (`GET .../facets` returning `{ facets: [] }` for an
 * uncaptured category) — both honestly render "no filters here," which is
 * the correct fallback either way.
 */
export function useCatalogFacets(
  category: string,
  selection: Readonly<Record<string, string>> = {},
  service: CatalogFacetsService = defaultCatalogFacetsService,
): CatalogFacetsState {
  const selectionKey = JSON.stringify(selection)
  // Same rationale `useCatalogSeries.ts` documents for the identical
  // pattern: re-keying on `selectionKey` (serialized CONTENT) rather than
  // `selection` itself keeps `stableSelection`'s reference stable across
  // renders whose selection is unchanged in content, so `load` isn't
  // recreated (and the effect doesn't re-run) on identity alone.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on `selectionKey`, not the `selection` object identity — that's the whole point of this memo (see the comment above).
  const stableSelection = useMemo(() => selection, [selectionKey])
  const hasSelection = Object.keys(stableSelection).length > 0
  const [state, setState] = useState<CatalogFacetsState>({ status: 'loading', facets: [] })
  const serviceRef = useRef(service)
  serviceRef.current = service

  const load = useCallback(() => {
    setState((prev) => ({ status: 'loading', facets: prev.facets }))
    let cancelled = false
    serviceRef.current
      .listFacets(category, hasSelection ? stableSelection : undefined)
      .then((catalogue) => {
        if (cancelled) return
        const reachableCeiling = reachableSeriesCeiling(catalogue.facets)
        setState({
          status: 'ready',
          facets: catalogue.facets.filter((facet) =>
            isSeriesFacetRenderable(facet, stableSelection, reachableCeiling),
          ),
        })
      })
      .catch(() => {
        if (cancelled) return
        setState({ status: 'ready', facets: [] })
      })
    return () => {
      cancelled = true
    }
    // `service` is read only via the ref, so `category`/`stableSelection`/
    // `hasSelection` are the only real dependencies.
  }, [category, stableSelection, hasSelection])

  useEffect(() => load(), [load])

  return state
}
